import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dueLabel } from '../../lib/quickAdd';
import { relative } from '../../lib/when';
import { fileSize } from '../ChatFile';
import { PRIORITIES } from './TodoEditor';
import { AiBadge } from './VoiceTodo';
import { whenWords } from './VoiceDrafts';
import { Icon } from './icons';
import './TodoAssist.css';
import './VoiceDrafts.css';
import './TodoEmails.css';

/*
 * EMAILS WAITING TO BE SORTED INTO TO-DOS (28 October, migration 106).
 *
 * Gordon: "When receiving an email in my inbox I can have multiple tasks,
 * multiple deadlines and multiple to-dos. Ideally when an email comes it
 * does not come in directly as a to-do but I have the option to use AI to
 * read and convert appropriately that email into different tasks where AI
 * gives me the suggested To-Do items and sub-items."
 *
 * So an email forwarded to jarvis@todo.ncmb.ai waits here. The page is two
 * halves: the waiting emails (newest first - from, subject, when, how many
 * files, "AI suggested 3 to-dos" or "No suggestions yet" or "AI is off"),
 * and, once one is open, the email itself (its text with quoted mail folded,
 * its files) beside the SUGGESTED TO-DOS as cards - VoiceDrafts' rows: a
 * tick to include, the title typed over, day, time, list, priority, notes,
 * and the SUB-ITEMS as lines of their own, each editable, added or removed.
 * A blank card can be added by hand; "Ask AI again" re-reads the email.
 * Then "Create the ticked to-dos" (sub-items as sub-tasks or as a
 * checklist; the email's files on the first, every or no to-do), "Make one
 * to-do from the whole email" (what arriving used to do), or Dismiss.
 * Created ones can be undone for a minute and a half, from the toast.
 *
 * AI OFF (the firm's switch, the person's AI switch, or no key on the
 * server): the same page, with no suggestions - blank cards to fill, Make
 * one, Dismiss - and a calm line saying so, with the way to Settings for an
 * administrator. Nothing of the email is sent anywhere while it is off.
 *
 * On a phone the two halves are one: the list, then the open email full
 * width with a Back button. KEYBOARD: Enter in a title creates the ticked
 * cards; Escape closes the email.
 *
 * Nothing here talks to the server directly: `api` is TodoApp's (so an
 * administrator looking as a contractor sees that contractor's emails, and
 * is told they are not theirs to sort - every button is then off).
 */

const CONFIDENCE = { high: 'Fairly sure', medium: 'Probably', low: 'Perhaps' };
const MAX_ITEMS = 20;
const MAX_STEPS = 20;
const plural = (n, one, many) => (n === 1 ? one : many);

/** The AI's reason for silence, in words; null when it is on. */
export const aiOffWords = (reason) => ({
  firm_off: 'AI suggestions are off: an administrator has switched them off for emails.',
  person_off: 'AI suggestions are off for you (Settings › Features).',
  not_configured: 'AI suggestions are off: this server has no key for Claude.'
}[reason] || 'AI suggestions are off.');

/** The three answers to "when an email of mine arrives", for the preference line. */
export const MODE_WORDS = [
  ['firm', 'Follow the firm setting'],
  ['sort', 'Wait to be sorted'],
  ['direct', 'Become a to-do straight away']
];

/**
 * The email's text, in two parts: what was written, and the quoted mail
 * underneath ("> ..." lines, or everything from an "On ..., X wrote:" line
 * or a "From:" header on), which starts folded.
 */
export function splitQuoted(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  let cut = lines.length;
  for (let i = 0; i < lines.length; i += 1) {
    const l = lines[i].trim();
    if (/^>/.test(l) || /^On .{3,120} wrote:$/.test(l) || /^-{2,}\s*(Original|Forwarded) message\s*-{2,}$/i.test(l)
      || (/^From:\s/.test(l) && lines[i + 1] && /^(Sent|To|Date):\s/.test(lines[i + 1].trim()))) { cut = i; break; }
  }
  const body = lines.slice(0, cut).join('\n').trim();
  const quoted = lines.slice(cut).join('\n').trim();
  return { body: body || (quoted ? '' : String(text || '').trim()), quoted: quoted || null };
}

/** A suggestion (or a blank) as a card row. */
let seq = 0;
const cardOf = (s = {}, { on = true } = {}) => ({
  rid: `c${(seq += 1)}`, on, open: false,
  title: s.title || '', notes: s.notes || null, due_date: s.due_date || null, due_time: s.due_time || null,
  deadline: s.deadline || null, priority: s.priority || 4, list_id: s.list_id || null,
  steps: Array.isArray(s.steps) ? s.steps.map((t) => (typeof t === 'string' ? t : t?.title || '')) : [],
  confidence: s.confidence || null, why: s.why || null, source: s.title ? 'ai' : 'hand'
});

export default function TodoEmails({ api, lists = [], narrow = false, plan = false, now = new Date(), wantEmail = null,
                                     onWaiting, onCreated, showToast, confirm, isAdmin = false, isContractor = false }) {
  const [summary, setSummary] = useState(null);   // GET /todos/emails
  const [failed, setFailed] = useState(null);
  const [openId, setOpenId] = useState(wantEmail || null);
  const [email, setEmail] = useState(null);       // the open one, whole
  const [cards, setCards] = useState([]);
  const [attach, setAttach] = useState('first');
  const [subItems, setSubItems] = useState('subtasks');
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  const [quotedOpen, setQuotedOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(true);
  const [savingPref, setSavingPref] = useState(false);
  const firstTitle = useRef(null);
  const activeLists = useMemo(() => lists.filter((l) => !l.archived_at && !l.private_hidden), [lists]);
  const inboxId = (lists.find((l) => l.is_inbox) || {}).id || null;

  const load = useCallback(async () => {
    try {
      const r = await api.get('/emails');
      setSummary(r.data);
      setFailed(null);
      if (onWaiting) onWaiting(r.data?.waiting ?? 0);
    } catch (err) {
      setFailed(err?.response?.status === 503 ? 'notready' : 'failed');
    }
  }, [api, onWaiting]);
  useEffect(() => { load(); }, [load]);

  /** Open one: fetch it whole and lay its suggestions out as cards. */
  const openOne = useCallback(async (id) => {
    setOpenId(id); setError(null); setQuotedOpen(false); setTextOpen(true);
    try {
      const r = await api.get(`/emails/${id}`);
      setEmail(r.data);
      setCards((r.data?.suggestions || []).map((s) => cardOf(s)));
      setAttach('first');
    } catch (err) {
      setEmail(null);
      setError(err?.response?.data?.error || 'Could not open that email');
    }
  }, [api]);
  useEffect(() => { if (openId) openOne(openId); }, [openId, openOne]);
  useEffect(() => { if (email && firstTitle.current) firstTitle.current.focus({ preventScroll: true }); }, [email]);

  const close = () => { setOpenId(null); setEmail(null); setCards([]); setError(null); };
  const patch = (rid, change) => setCards((cs) => cs.map((c) => (c.rid === rid ? { ...c, ...change } : c)));
  const setStep = (rid, i, value) => setCards((cs) => cs.map((c) => (c.rid === rid ? { ...c, steps: c.steps.map((s, j) => (j === i ? value : s)) } : c)));
  const dropStep = (rid, i) => setCards((cs) => cs.map((c) => (c.rid === rid ? { ...c, steps: c.steps.filter((_s, j) => j !== i) } : c)));
  const addStep = (rid) => setCards((cs) => cs.map((c) => (c.rid === rid && c.steps.length < MAX_STEPS ? { ...c, steps: [...c.steps, ''], open: true } : c)));
  const addCard = () => {
    if (cards.length >= MAX_ITEMS) return;
    const c = cardOf({}, { on: true });
    setCards((cs) => [...cs, c]);
    setTimeout(() => document.querySelector(`[data-tem-card="${c.rid}"] .tvl-title`)?.focus(), 0);
  };
  const kept = cards.filter((c) => c.on && c.title.trim());
  const own = summary?.own !== false;

  const failWords = (err, fallback) => err?.response?.data?.error || fallback;

  /** After a sort of any kind: off the list, the count down, the toast with Undo. */
  const sorted = (id, words, undoable = true) => {
    setSummary((s) => (s ? { ...s, waiting: Math.max(0, (s.waiting || 1) - 1), emails: (s.emails || []).filter((e) => e.id !== id) } : s));
    if (onWaiting && summary) onWaiting(Math.max(0, (summary.waiting || 1) - 1));
    close();
    if (onCreated) onCreated();
    if (showToast) {
      showToast(words, undoable ? {
        undo: async () => {
          try {
            await api.post(`/emails/${id}/undo`, {});
            await load();
            if (onCreated) onCreated();
            setOpenId(id);
          } catch (err) { setError(failWords(err, 'Could not undo that')); }
        }
      } : {});
    }
  };

  const create = async () => {
    if (busy || !kept.length || !own) return;
    setBusy(true); setError(null);
    try {
      const items = kept.map((c) => ({
        title: c.title.trim(), notes: c.notes || null, due_date: c.due_date || null, due_time: c.due_time || null,
        priority: c.priority, list_id: c.list_id || null, deadline: plan ? c.deadline || null : null,
        steps: c.steps.map((s) => s.trim()).filter(Boolean)
      }));
      const r = await api.post(`/emails/${email.id}/create`, { items, attach, sub_items: subItems });
      const n = (r.data?.created || []).length;
      sorted(email.id, `${n} ${plural(n, 'to-do', 'to-dos')} made from “${email.subject || '(no subject)'}”`);
    } catch (err) { setError(failWords(err, 'Could not make those to-dos')); }
    finally { setBusy(false); }
  };
  const makeOne = async () => {
    if (busy || !own) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/emails/${email.id}/one`, {});
      sorted(email.id, `One to-do made from “${email.subject || '(no subject)'}”`);
    } catch (err) { setError(failWords(err, 'Could not make that to-do')); }
    finally { setBusy(false); }
  };
  const dismiss = async () => {
    if (busy || !own) return;
    const answer = confirm ? await confirm({
      title: 'Dismiss this email?', confirmLabel: 'Dismiss', danger: true,
      message: `Nothing will be made from “${email.subject || '(no subject)'}”, and its ${email.files?.length ? 'files are' : 'text is'} dropped. The email itself stays in your mailbox.`,
      prompt: { label: 'Why (optional)', placeholder: 'Only a newsletter', initial: '' }
    }) : 'x';
    if (answer === false) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/emails/${email.id}/dismiss`, { reason: typeof answer === 'string' && answer.trim() ? answer.trim() : undefined });
      sorted(email.id, `Dismissed “${email.subject || '(no subject)'}”`, false);
    } catch (err) { setError(failWords(err, 'Could not dismiss that email')); }
    finally { setBusy(false); }
  };
  const askAgain = async () => {
    if (asking || !own) return;
    setAsking(true); setError(null);
    try {
      const r = await api.post(`/emails/${email.id}/suggest`, {});
      setEmail(r.data.email);
      setCards((r.data.email?.suggestions || []).map((s) => cardOf(s)));
      setSummary((s) => (s ? { ...s, emails: (s.emails || []).map((e) => (e.id === email.id ? { ...e, suggestion_count: (r.data.email?.suggestions || []).length, suggestion_error: null } : e)) } : s));
    } catch (err) {
      if (err?.response?.status === 429) setError(err.response.data?.error || 'That has been asked a few times already - try again in a while.');
      else setError(failWords(err, 'Claude could not read this email just now. It is still here - sort it by hand or try again.'));
    } finally { setAsking(false); }
  };
  /** A file's bytes, fetched signed in (an <a href> could carry no token), then saved. */
  const saveFile = async (f) => {
    try {
      const blob = await api.blob(`/emails/${email.id}/files/${encodeURIComponent(f.id)}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.file_name || 'file';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) { setError(failWords(err, 'Could not fetch that file')); }
  };
  const savePref = async (mode) => {
    setSavingPref(true);
    try {
      const r = await api.put('/emails/prefs', { email_mode: mode });
      setSummary((s) => (s ? { ...s, ...r.data } : s));
    } catch (err) { setError(failWords(err, 'Could not save that preference')); }
    finally { setSavingPref(false); }
  };

  /* ------------------------------------------------------------- pieces */

  if (failed === 'notready') {
    return (
      <div className="tem" data-tem-notready="">
        <p className="ctd-quiet">Sorting emails into to-dos is waiting for a database update on the server (<code>npm run migrate:106</code>). Until then every email becomes a to-do straight away.</p>
      </div>
    );
  }
  if (failed) return <div className="tem"><p className="tds-error" role="alert">Could not load the waiting emails.</p></div>;
  if (!summary) return <div className="tem"><p className="ctd-quiet">Loading…</p></div>;

  const settingsLink = isAdmin && !isContractor ? '/settings/content/calendars#todo-email-arrives' : null;
  const aiLine = !summary.ai && (
    <p className="tem-ai-off" data-tem-ai-off={summary.ai_reason || 'off'} role="note">
      <Icon name="sparkle" size={13} /> {aiOffWords(summary.ai_reason)}
      {' '}Sort by hand: add cards, or make one to-do of the whole email.
      {settingsLink && <> <Link to={settingsLink}>Settings</Link></>}
    </p>
  );

  const prefLine = own && (
    <label className="tem-pref" data-tem-pref="">
      <span>Emails I send in:</span>
      <select value={summary.personal_mode || 'firm'} disabled={savingPref} onChange={(e) => savePref(e.target.value)} aria-label="What happens to an email I send in">
        {MODE_WORDS.map(([k, w]) => <option key={k} value={k}>{k === 'firm' ? `${w} (${summary.firm_mode === 'direct' ? 'become a to-do straight away' : 'wait to be sorted'})` : w}</option>)}
      </select>
    </label>
  );

  const rowWords = (e) => (!summary.ai ? 'AI is off'
    : e.suggestion_error && e.suggestion_count === null ? 'AI could not read it'
      : e.suggestion_count === null ? 'No suggestions yet'
        : e.suggestion_count === 0 ? 'AI found nothing to do'
          : `AI suggested ${e.suggestion_count} ${plural(e.suggestion_count, 'to-do', 'to-dos')}`);

  const list = (
    <div className="tem-list" data-tem-list="" role="list" aria-label="Emails waiting to be sorted">
      {summary.emails.length === 0 && (
        <p className="ctd-quiet tem-empty" data-tem-empty="">
          {summary.mode === 'direct'
            ? 'Nothing waiting. Your emails become to-dos straight away.'
            : 'Nothing waiting. Forward an email to jarvis@todo.ncmb.ai and it appears here to be sorted.'}
        </p>
      )}
      {summary.emails.map((e) => (
        <button key={e.id} type="button" role="listitem" className={`tem-row${e.id === openId ? ' is-on' : ''}`} data-tem-row={e.id}
                aria-current={e.id === openId ? 'true' : undefined} onClick={() => openOne(e.id)}>
          <span className="tem-row-top">
            <span className="tem-from">{e.from_name || e.from_address}</span>
            <time className="tem-when" dateTime={e.received_at} title={new Date(e.received_at).toLocaleString('en-GB')}>{relative(e.received_at, now)}</time>
          </span>
          <span className="tem-subject">{e.subject || '(no subject)'}</span>
          <span className="tem-row-meta">
            {e.attachment_count > 0 && <span className="tem-files"><Icon name="paperclip" size={12} />{e.attachment_count}</span>}
            <span className={`tem-ai${e.suggestion_count > 0 ? ' is-some' : ''}`}>{rowWords(e)}</span>
          </span>
        </button>
      ))}
    </div>
  );

  const detail = openId && (
    <section className="tem-detail" data-tem-open={openId} aria-label="The email and its suggested to-dos"
             onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } }}>
      {!email && !error && <p className="ctd-quiet">Loading…</p>}
      {error && !email && <p className="tds-error" role="alert">{error}</p>}
      {email && (() => {
        const { body, quoted } = splitQuoted(email.text);
        return (
          <>
            <div className="tem-head">
              {narrow && <button type="button" className="tds-icon-btn tem-back" aria-label="Back to the list" onClick={close}><Icon name="chevronLeft" size={16} /></button>}
              <div className="tem-head-words">
                <h3 className="tem-title">{email.subject || '(no subject)'}</h3>
                <p className="tem-meta">
                  From {email.from_name ? <><strong>{email.from_name}</strong> ({email.from_address})</> : <strong>{email.from_address}</strong>}
                  {' · '}<time dateTime={email.received_at}>{relative(email.received_at, now)}</time>
                </p>
              </div>
              {!narrow && <button type="button" className="tds-icon-btn" aria-label="Close (Esc)" title="Close (Esc)" onClick={close}><Icon name="close" size={15} /></button>}
            </div>
            <div className="tem-split">
              <div className="tem-mail">
                <button type="button" className="tds-link tem-fold" aria-expanded={textOpen} onClick={() => setTextOpen((v) => !v)}>
                  <Icon name={textOpen ? 'chevronDown' : 'chevronRight'} size={12} /> The email
                </button>
                {textOpen && (
                  <div className="tem-text" data-tem-text="">
                    {body ? <pre>{body}</pre> : <p className="tds-quiet">(no text)</p>}
                    {quoted && (
                      <>
                        <button type="button" className="tds-link tem-quoted-toggle" aria-expanded={quotedOpen} data-tem-quoted="" onClick={() => setQuotedOpen((v) => !v)}>
                          {quotedOpen ? 'Hide quoted text' : 'Show quoted text'}
                        </button>
                        {quotedOpen && <pre className="tem-quoted">{quoted}</pre>}
                      </>
                    )}
                  </div>
                )}
                {email.files?.length > 0 && (
                  <ul className="tem-attachments" data-tem-files="" aria-label="Attachments">
                    {email.files.map((f) => (
                      <li key={f.id}>
                        <button type="button" className="tem-file" data-tem-file={f.id} title={`Download ${f.file_name}`} onClick={() => saveFile(f)}>
                          <Icon name={f.kind === 'image' ? 'image' : 'paperclip'} size={13} />
                          <span className="tem-file-name">{f.file_name}</span>
                          <span className="tds-quiet">{fileSize(f.size_bytes)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="tem-cards tvl" data-tem-cards={cards.length}>
                <div className="tva-card-head">
                  {summary.ai && cards.some((c) => c.source === 'ai') ? <AiBadge /> : null}
                  <span className="tva-card-title">
                    {cards.length ? `${cards.length} ${plural(cards.length, 'to-do', 'to-dos')} to make` : summary.ai && email.suggestions === null ? 'No suggestions yet' : summary.ai && email.suggestions ? 'AI found nothing to do here' : 'Add the to-dos this email needs'}
                  </span>
                  {summary.ai && own && (
                    <button type="button" className="tds-link tem-again" data-tem-again="" disabled={asking} onClick={askAgain}>
                      <Icon name="sparkle" size={13} /> {asking ? 'Asking…' : email.suggestions === null ? 'Ask AI' : 'Ask AI again'}
                    </button>
                  )}
                </div>
                {email.suggestion_error && !cards.length && <p className="tva-note" role="status">Claude could not read this one last time ({email.suggestion_error}).</p>}
                {aiLine}
                {error && <p className="tds-error" role="alert">{error}</p>}

                <ol className="tvl-list">
                  {cards.map((c, i) => {
                    const listId = c.list_id || inboxId;
                    const list_ = activeLists.find((l) => l.id === listId) || null;
                    return (
                      <li key={c.rid} className={`tvl-row tem-card${c.on ? '' : ' is-off'}`} data-tem-card={c.rid} data-tem-source={c.source}>
                        <div className="tvl-top">
                          <input type="checkbox" className="tvl-check" checked={c.on} aria-label={`Include "${c.title || `to-do ${i + 1}`}"`} onChange={(e) => patch(c.rid, { on: e.target.checked })} />
                          <input ref={i === 0 ? firstTitle : null} type="text" className="tvl-title" value={c.title} maxLength={300} placeholder="What needs doing"
                                 aria-label={`To-do ${i + 1}`} disabled={!own}
                                 onChange={(e) => patch(c.rid, { title: e.target.value })}
                                 onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); create(); } }} />
                          <button type="button" className="tds-icon-btn tvl-remove" aria-label={`Remove "${c.title || `to-do ${i + 1}`}"`} title="Remove this card"
                                  onClick={() => setCards((cs) => cs.filter((x) => x.rid !== c.rid))}><Icon name="close" size={14} /></button>
                        </div>
                        <div className="tvl-fields">
                          <label className="tvl-field"><span>Day</span>
                            <input type="date" value={c.due_date || ''} aria-label={`Day of to-do ${i + 1}`} onChange={(e) => patch(c.rid, { due_date: e.target.value || null })} />
                          </label>
                          <label className="tvl-field is-time"><span>Time</span>
                            <input type="time" value={c.due_time || ''} aria-label={`Time of to-do ${i + 1}`} onChange={(e) => patch(c.rid, { due_time: e.target.value || null })} />
                          </label>
                          {plan && (
                            <label className="tvl-field"><span>Deadline</span>
                              <input type="date" value={c.deadline || ''} aria-label={`Deadline of to-do ${i + 1}`} onChange={(e) => patch(c.rid, { deadline: e.target.value || null })} />
                            </label>
                          )}
                          <label className="tvl-field"><span>Priority</span>
                            <select value={String(c.priority)} aria-label={`Priority of to-do ${i + 1}`} onChange={(e) => patch(c.rid, { priority: Number(e.target.value) })}>
                              {PRIORITIES.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
                            </select>
                          </label>
                          <label className="tvl-field is-list"><span>List</span>
                            <select value={listId || ''} aria-label={`List of to-do ${i + 1}`} onChange={(e) => patch(c.rid, { list_id: e.target.value || null })}>
                              {activeLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          </label>
                        </div>
                        <div className="tva-chips tvl-chips">
                          {c.due_date && <span className="ctd-chip is-date" data-chip="date"><Icon name="calendar" size={12} />{whenWords(c.due_date, c.due_time, null, now)}</span>}
                          {plan && c.deadline && <span className="ctd-chip is-deadline" data-chip="deadline"><Icon name="deadline" size={12} />By {dueLabel(c.deadline, null, now)}</span>}
                          {c.priority < 4 && <span className={`ctd-chip is-priority p${c.priority}`} data-chip="priority"><Icon name="flag" size={12} />P{c.priority}</span>}
                          {list_ && list_.id !== inboxId && <span className="ctd-chip is-list" data-chip="list"><Icon name="hash" size={12} />{list_.name}</span>}
                          {c.confidence && <span className={`tem-conf is-${c.confidence}`} data-tem-conf={c.confidence} title={c.why || undefined}>{CONFIDENCE[c.confidence] || c.confidence}</span>}
                          <button type="button" className="tds-link tvl-more" aria-expanded={c.open} onClick={() => patch(c.rid, { open: !c.open })}>
                            {c.open ? 'Less' : c.steps.length ? `Notes · ${c.steps.length} ${plural(c.steps.length, 'sub-item', 'sub-items')}` : 'Notes · sub-items'}
                          </button>
                        </div>
                        {c.why && !c.open && <p className="tem-why" data-tem-why="">{c.why}</p>}
                        {c.open && (
                          <div className="tva-fields tvl-extra tem-extra">
                            <label className="tds-field tva-wide"><span>Notes</span>
                              <textarea rows={2} value={c.notes || ''} maxLength={4000} onChange={(e) => patch(c.rid, { notes: e.target.value || null })} />
                            </label>
                            <fieldset className="tem-steps tva-wide" data-tem-steps={c.steps.length}>
                              <legend>Sub-items</legend>
                              {c.steps.map((s, j) => (
                                <div key={j} className="tem-step">
                                  <Icon name="indent" size={12} />
                                  <input type="text" value={s} maxLength={300} aria-label={`Sub-item ${j + 1} of to-do ${i + 1}`} placeholder="A step"
                                         onChange={(e) => setStep(c.rid, j, e.target.value)}
                                         onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); addStep(c.rid); } }} />
                                  <button type="button" className="tds-icon-btn" aria-label={`Remove sub-item ${j + 1}`} onClick={() => dropStep(c.rid, j)}><Icon name="close" size={12} /></button>
                                </div>
                              ))}
                              {c.steps.length < MAX_STEPS && (
                                <button type="button" className="tds-link tem-add-step" data-tem-add-step="" onClick={() => addStep(c.rid)}><Icon name="plus" size={12} /> Add a sub-item</button>
                              )}
                            </fieldset>
                            {c.why && <p className="tem-why" data-tem-why="">{c.why}</p>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {own && cards.length < MAX_ITEMS && (
                  <button type="button" className="tds-link tem-add" data-tem-add="" onClick={addCard}><Icon name="plus" size={13} /> Add a to-do</button>
                )}

                {own && (
                  <div className="tem-options">
                    <label className="tem-opt"><span>Sub-items as</span>
                      <select value={subItems} data-tem-sub="" onChange={(e) => setSubItems(e.target.value)} aria-label="How sub-items are made">
                        <option value="subtasks">Sub-tasks</option>
                        <option value="checklist">A checklist</option>
                      </select>
                    </label>
                    {email.files?.length > 0 && (
                      <label className="tem-opt"><span>Attach the email’s files to</span>
                        <select value={attach} data-tem-attach="" onChange={(e) => setAttach(e.target.value)} aria-label="Which to-dos get the email's files">
                          <option value="first">The first to-do</option>
                          <option value="every">Every to-do</option>
                          <option value="none">None</option>
                        </select>
                      </label>
                    )}
                  </div>
                )}
                {!own && <p className="tds-quiet" data-tem-not-own="">Only the person this email was sent to can sort it.</p>}
                <div className="tva-card-foot tem-foot">
                  <button type="button" className="tds-link is-danger" data-tem-dismiss="" disabled={busy || !own} onClick={dismiss}><Icon name="trash" size={13} /> Dismiss</button>
                  <span className="tva-spacer" />
                  <button type="button" className="btn btn-outline tds-btn" data-tem-one="" disabled={busy || !own} onClick={makeOne} title="One to-do: the subject as its title, the email as its notes, the files on it">
                    Make one to-do from the whole email
                  </button>
                  <button type="button" className="btn btn-primary tds-btn" data-tem-create="" disabled={busy || !own || !kept.length} onClick={create}>
                    {kept.length === 0 ? 'Create the ticked to-dos' : kept.length === 1 ? 'Create 1 to-do' : `Create ${kept.length} to-dos`}
                  </button>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </section>
  );

  return (
    <div className={`tem${openId ? ' has-open' : ''}${narrow ? ' is-narrow' : ''}`} data-tem="" data-tem-mode={summary.mode} data-tem-ai={summary.ai ? 'on' : 'off'}>
      <div className="tem-top">
        <p className="tem-lead">
          {summary.waiting === 0 ? 'No emails are waiting.' : `${summary.waiting} ${plural(summary.waiting, 'email is', 'emails are')} waiting to be sorted into to-dos.`}
          {summary.mode === 'direct' && own && <> Yours become to-dos straight away.</>}
        </p>
        {prefLine}
      </div>
      {!openId && aiLine}
      {error && !openId && <p className="tds-error" role="alert">{error}</p>}
      <div className="tem-body">
        {(!narrow || !openId) && list}
        {detail}
      </div>
    </div>
  );
}
